import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { Box, Column, Row } from '@shipt/design-system-layouts'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { type StoreCard, type Link } from '@/features/cms/components/CMS/types'
import { hideScrollBar } from '@/features/shared/styles/effects'
import styled from 'styled-components'
import { Headline } from '@shipt/design-system-typography'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { SegmentedControl } from '@shipt/design-system-segmented-control'
import { Avatar } from '@shipt/design-system-avatar'
import { ArrowRightIcon } from '@shipt/design-system-icons'
import { IconButtonLink, TextButtonLink } from '@/features/shared/elements/Link/Buttons'
import { useGlobalHomeSelectStore } from '@/features/shop/components/GlobalHomePage/useGlobalHomeSelectStore'
import { useDeliveryStoreById } from '@/features/shop/services/ShoppingStore/hooks'
import { getRetailerBaseUrl } from '@shared/constants/routes'
import { theme } from '@/features/shared/constants/shiptTheme'

export const MultiStoreHeader = ({
  stores,
  selectedIndex,
  onStoreSelect,
  heading,
  link,
  analyticSlug,
}: {
  stores: StoreCard['data'][]
  selectedIndex: number
  onStoreSelect: (index: number) => void
  heading?: string
  link?: Link
  analyticSlug?: string
}) => {
  const { trackCMSElementClicked } = useCMSTrackingEvents()
  const { selectStore } = useGlobalHomeSelectStore()

  const storeId = stores[selectedIndex]?.retailer_id
  const retailer = useDeliveryStoreById(storeId ?? null)

  const normalizedUrl = getRetailerBaseUrl(retailer?.name, link?.url)

  const handleViewAll = () => {
    trackCMSElementClicked({
      type: 'link',
      content: 'View all',
      shelf_name: heading,
      shelf_index: selectedIndex,
    })

    if (retailer) {
      return selectStore(retailer, normalizedUrl)
    }
  }

  const handleStoreClick = (store: StoreCard['data'] | undefined, index: number) => {
    trackCMSElementClicked({
      content: store?.name,
      list_index: index,
      display_format: DISPLAY_FORMAT.STORE_BUTTON,
      display_sublocation: analyticSlug,
    })
  }

  return (
    <Column padding={{ base: [0, 'lg'], md: [0] }}>
      <Row justify="space-between">
        {!!heading && (
          <StoreTitles as="h2" size="lg">
            {heading}
          </StoreTitles>
        )}
        {!!normalizedUrl && !!retailer && (
          <>
            <Row display={{ base: 'flex', md: 'none' }} align="center">
              <IconButtonLink
                href={normalizedUrl}
                onClick={(e) => {
                  e.preventDefault()
                  handleViewAll()
                }}
                aria-label={`view all ${heading} for ${stores[selectedIndex]?.name}`}
                variant="tertiary"
                icon={ArrowRightIcon}
                size="sm"
              />
            </Row>
            <Row display={{ base: 'none', md: 'flex' }}>
              <TextButtonLink
                href={normalizedUrl}
                onClick={(e) => {
                  e.preventDefault()
                  handleViewAll()
                }}
                aria-label={`view all ${heading} for ${stores[selectedIndex]?.name}`}
              >
                View all
              </TextButtonLink>
            </Row>
          </>
        )}
      </Row>
      <SegmentedControl
        isContained={false}
        value={String(selectedIndex)}
        onChange={(value) => {
          const index = Number(value)
          onStoreSelect(index)
          handleStoreClick(stores[index], index)
        }}
      >
        <StoreTilesWrapper padding={['xs', 0, 'xs', 'xs']}>
          <SegmentedControl.RadioGroup aria-label="Select store to view products">
            {stores.map((store, index) => (
              <CustomRadio key={store.retailer_id} value={String(index)}>
                <Avatar
                  colorScheme="retailer"
                  image={
                    <Image src={store.logo.src} alt={`${store.name} logo`} width={64} height={64} />
                  }
                  retailerColor={store.background_color ?? ''}
                  hasBorder
                  size="xl"
                  type="image"
                />
              </CustomRadio>
            ))}
          </SegmentedControl.RadioGroup>
        </StoreTilesWrapper>
      </SegmentedControl>
    </Column>
  )
}

const CustomRadio = styled(SegmentedControl.CustomRadio)`
  position: relative;

  &:hover::after {
    background-color: rgb(0 0 0 / 10%);
    border-radius: 12px;
    content: '';
    pointer-events: none;
    position: absolute;
    inset: 0;
  }

  &&[aria-checked='true'] {
    border-radius: 12px;
    outline: 2px solid ${theme.color.focus.default};
    outline-offset: 2px;
  }

  & * {
    border-width: 1px;
  }
`

const StoreTilesWrapper = styled(Box)`
  margin: -4px 0;
  overflow-x: auto;
  ${hideScrollBar}
`

const StoreTitles = styled(Headline)`
  margin-bottom: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
